import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Grid,
    Chip,
    Paper,
    ButtonGroup,
    Button,
    Tooltip
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';

// icons
import {
    IconChartLine,
    IconChartBar,
    IconChartArea,
    IconClock,
    IconAlertCircle,
    IconCircleCheck,
    IconFunction,
    IconTrendingUp,
    IconTrendingDown
} from '@tabler/icons-react';

// ==============================|| METRICS CHART ||============================== //

const MetricsChart = ({ functions }) => {
    const theme = useTheme();
    const [selectedMetric, setSelectedMetric] = useState('executions');
    const [timeRange, setTimeRange] = useState('24h');
    const [selectedFunction, setSelectedFunction] = useState('all');
    const [chartType, setChartType] = useState('line');

    // Generate mock time series data
    const generateTimeSeriesData = () => {
        const now = new Date();
        const data = [];
        const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;

        for (let i = hours; i >= 0; i--) {
            const timestamp = new Date(now - i * 60 * 60 * 1000);
            let value = 0;

            switch (selectedMetric) {
                case 'executions':
                    value = Math.floor(Math.random() * 1000) + 500;
                    break;
                case 'errors':
                    value = Math.floor(Math.random() * 50) + 5;
                    break;
                case 'latency':
                    value = Math.floor(Math.random() * 100) + 20;
                    break;
                case 'success_rate':
                    value = 95 + Math.random() * 4;
                    break;
                default:
                    value = Math.floor(Math.random() * 100);
            }

            data.push({
                x: timestamp.getTime(),
                y: value
            });
        }

        return data;
    };

    // Chart options
    const getChartOptions = () => {
        const isDark = theme.palette.mode === 'dark';

        return {
            chart: {
                type: chartType,
                height: 400,
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true
                    }
                },
                background: 'transparent',
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800
                }
            },
            colors: [
                selectedMetric === 'errors'
                    ? theme.palette.error.main
                    : selectedMetric === 'success_rate'
                      ? theme.palette.success.main
                      : theme.palette.primary.main
            ],
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth',
                width: 2
            },
            fill: {
                type: chartType === 'area' ? 'gradient' : 'solid',
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.3,
                    stops: [0, 90, 100]
                }
            },
            grid: {
                borderColor: isDark ? '#333' : '#e0e0e0',
                strokeDashArray: 4
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    style: {
                        colors: theme.palette.text.secondary
                    }
                }
            },
            yaxis: {
                title: {
                    text: getMetricLabel(selectedMetric),
                    style: {
                        color: theme.palette.text.secondary
                    }
                },
                labels: {
                    style: {
                        colors: theme.palette.text.secondary
                    },
                    formatter: (value) => {
                        if (selectedMetric === 'success_rate') {
                            return value.toFixed(1) + '%';
                        }
                        if (selectedMetric === 'latency') {
                            return value + 'ms';
                        }
                        return Math.round(value).toLocaleString();
                    }
                }
            },
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                x: {
                    format: 'dd MMM HH:mm'
                },
                y: {
                    formatter: (value) => {
                        if (selectedMetric === 'success_rate') {
                            return value.toFixed(2) + '%';
                        }
                        if (selectedMetric === 'latency') {
                            return value + 'ms';
                        }
                        return Math.round(value).toLocaleString();
                    }
                }
            }
        };
    };

    const getMetricLabel = (metric) => {
        switch (metric) {
            case 'executions':
                return 'Total Executions';
            case 'errors':
                return 'Error Count';
            case 'latency':
                return 'Average Latency (ms)';
            case 'success_rate':
                return 'Success Rate (%)';
            default:
                return metric;
        }
    };

    // Calculate summary statistics
    const calculateStats = () => {
        const deployedFunctions = functions.filter((f) => f.status === 'deployed');
        const totalExecutions = deployedFunctions.reduce((sum, f) => sum + f.metrics.total_executions_24h, 0);
        const totalErrors = deployedFunctions.reduce((sum, f) => sum + f.metrics.errors_24h, 0);
        const avgLatency = deployedFunctions.reduce((sum, f) => sum + f.metrics.avg_latency_ms, 0) / deployedFunctions.length || 0;
        const avgSuccessRate = deployedFunctions.reduce((sum, f) => sum + f.metrics.success_rate, 0) / deployedFunctions.length || 0;

        return {
            totalExecutions,
            totalErrors,
            avgLatency: avgLatency.toFixed(1),
            avgSuccessRate: avgSuccessRate.toFixed(1),
            errorRate: ((totalErrors / totalExecutions) * 100).toFixed(2)
        };
    };

    const stats = calculateStats();

    const series = [
        {
            name: getMetricLabel(selectedMetric),
            data: generateTimeSeriesData()
        }
    ];

    return (
        <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Total Executions
                                    </Typography>
                                    <Typography variant="h4">{stats.totalExecutions.toLocaleString()}</Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                        <IconTrendingUp size={16} color={theme.palette.success.main} />
                                        <Typography variant="caption" color="success.main">
                                            +12.5%
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconChartLine size={32} style={{ opacity: 0.3 }} />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Success Rate
                                    </Typography>
                                    <Typography variant="h4" color="success.main">
                                        {stats.avgSuccessRate}%
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                        <IconCircleCheck size={16} color={theme.palette.success.main} />
                                        <Typography variant="caption" color="textSecondary">
                                            Healthy
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconCircleCheck size={32} style={{ opacity: 0.3, color: theme.palette.success.main }} />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Avg Latency
                                    </Typography>
                                    <Typography variant="h4">{stats.avgLatency}ms</Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                        <IconTrendingDown size={16} color={theme.palette.success.main} />
                                        <Typography variant="caption" color="success.main">
                                            -8.3%
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconClock size={32} style={{ opacity: 0.3 }} />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Error Rate
                                    </Typography>
                                    <Typography variant="h4" color={parseFloat(stats.errorRate) > 5 ? 'error.main' : 'text.primary'}>
                                        {stats.errorRate}%
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                        <Typography variant="caption" color="textSecondary">
                                            {stats.totalErrors} errors
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconAlertCircle size={32} style={{ opacity: 0.3, color: theme.palette.error.main }} />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>

            {/* Main Chart */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                            <Typography variant="h4">Performance Metrics</Typography>

                            <Box display="flex" gap={2} alignItems="center">
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <Select value={selectedFunction} onChange={(e) => setSelectedFunction(e.target.value)}>
                                        <MenuItem value="all">All Functions</MenuItem>
                                        {functions
                                            .filter((f) => f.status === 'deployed')
                                            .map((func) => (
                                                <MenuItem key={func.id} value={func.id}>
                                                    {func.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <Select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)}>
                                        <MenuItem value="executions">Executions</MenuItem>
                                        <MenuItem value="errors">Errors</MenuItem>
                                        <MenuItem value="latency">Latency</MenuItem>
                                        <MenuItem value="success_rate">Success Rate</MenuItem>
                                    </Select>
                                </FormControl>

                                <ButtonGroup size="small">
                                    <Button variant={timeRange === '24h' ? 'contained' : 'outlined'} onClick={() => setTimeRange('24h')}>
                                        24H
                                    </Button>
                                    <Button variant={timeRange === '7d' ? 'contained' : 'outlined'} onClick={() => setTimeRange('7d')}>
                                        7D
                                    </Button>
                                    <Button variant={timeRange === '30d' ? 'contained' : 'outlined'} onClick={() => setTimeRange('30d')}>
                                        30D
                                    </Button>
                                </ButtonGroup>

                                <ButtonGroup size="small">
                                    <Tooltip title="Line Chart">
                                        <Button
                                            variant={chartType === 'line' ? 'contained' : 'outlined'}
                                            onClick={() => setChartType('line')}
                                        >
                                            <IconChartLine size={18} />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Area Chart">
                                        <Button
                                            variant={chartType === 'area' ? 'contained' : 'outlined'}
                                            onClick={() => setChartType('area')}
                                        >
                                            <IconChartArea size={18} />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Bar Chart">
                                        <Button
                                            variant={chartType === 'bar' ? 'contained' : 'outlined'}
                                            onClick={() => setChartType('bar')}
                                        >
                                            <IconChartBar size={18} />
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>
                            </Box>
                        </Box>

                        <ReactApexChart options={getChartOptions()} series={series} type={chartType} height={400} />
                    </CardContent>
                </Card>
            </Grid>

            {/* Function Performance Table */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Function Performance Summary
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                            {functions
                                .filter((f) => f.status === 'deployed')
                                .map((func, index) => (
                                    <Box
                                        key={func.id}
                                        sx={{
                                            p: 2,
                                            mb: 1,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <Grid container alignItems="center" spacing={2}>
                                            <Grid item xs={12} md={3}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <IconFunction size={16} />
                                                    <Typography variant="subtitle2">{func.name}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Executions
                                                </Typography>
                                                <Typography variant="body2">
                                                    {func.metrics.total_executions_24h.toLocaleString()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Success Rate
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color={func.metrics.success_rate > 95 ? 'success.main' : 'warning.main'}
                                                >
                                                    {func.metrics.success_rate}%
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Avg Latency
                                                </Typography>
                                                <Typography variant="body2">{func.metrics.avg_latency_ms}ms</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Typography variant="caption" color="textSecondary">
                                                    P99 Latency
                                                </Typography>
                                                <Typography variant="body2">{func.metrics.p99_latency_ms}ms</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={1}>
                                                <Chip
                                                    label={func.metrics.errors_24h > 0 ? `${func.metrics.errors_24h} errors` : 'Healthy'}
                                                    size="small"
                                                    color={func.metrics.errors_24h > 0 ? 'error' : 'success'}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default MetricsChart;
